import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Play, Youtube, Instagram, ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const fmt = (d) => (d ? new Date(d.replace("+0000", "+00:00")).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

const VideoCard = ({ v }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="bg-white group" data-testid={`yt-video-${v.id}`}>
      <div className="relative aspect-video bg-black overflow-hidden">
        {playing ? (
          <iframe src={`https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&rel=0`} title={v.title} className="absolute inset-0 w-full h-full" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen data-testid={`yt-player-${v.id}`} />
        ) : (
          <button type="button" onClick={() => setPlaying(true)} className="absolute inset-0 w-full h-full" aria-label={`Play ${v.title}`} data-testid={`yt-play-${v.id}`}>
            <img src={v.thumbnail} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors">
              <span className="w-14 h-14 rounded-full bg-[#FFC72C] flex items-center justify-center shadow-lg transition-transform group-hover:scale-110">
                <Play className="w-6 h-6 text-black ml-1" fill="currentColor" />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-sm text-[#09090b] line-clamp-2">{v.title}</h3>
        <p className="text-xs text-gray-500 mt-1">{fmt(v.published)}</p>
      </div>
    </div>
  );
};

const PostCard = ({ p }) => (
  <a href={p.url} target="_blank" rel="noopener noreferrer" className="bg-white group block" data-testid={`ig-post-${p.id}`}>
    <div className="aspect-square bg-gray-100 overflow-hidden">
      {p.image && <img src={p.image} alt={p.caption?.slice(0, 100) || "Instagram post"} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
    </div>
    <div className="p-4">
      <p className="text-sm text-[#09090b] line-clamp-2">{p.caption || "View on Instagram"}</p>
      <p className="text-xs text-gray-500 mt-1">{fmt(p.published)}</p>
    </div>
  </a>
);

const Row = ({ icon: Icon, title, href, linkLabel, testid, children }) => (
  <div className="mb-12 last:mb-0" data-testid={testid}>
    <div className="flex items-center justify-between mb-6">
      <h3 className="flex items-center gap-2 font-heading text-xl font-bold uppercase text-[#09090b]"><Icon className="w-5 h-5 text-[#009E60]" /> {title}</h3>
      <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#009E60] hover:text-[#008050] font-medium text-sm" data-testid={`${testid}-link`}>
        {linkLabel} <ArrowRight className="w-4 h-4" />
      </a>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{children}</div>
  </div>
);

export const SocialFeed = () => {
  const [feed, setFeed] = useState(null);
  useEffect(() => {
    axios.get(`${API}/social/latest`).then((r) => setFeed(r.data)).catch(() => setFeed({ youtube: [], instagram: [] }));
  }, []);

  if (feed && !feed.youtube.length && !feed.instagram.length) return null;

  return (
    <section className="py-16 px-4 md:px-8" data-testid="social-feed">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-heading text-3xl font-bold uppercase text-[#09090b] mb-8">Latest From ABFA</h2>
        {!feed ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="aspect-video w-full" />)}</div>
        ) : (
          <>
            {feed.youtube.length > 0 && (
              <Row icon={Youtube} title="Latest Videos" href={feed.youtube_channel} linkLabel="YouTube Channel" testid="social-youtube-row">
                {feed.youtube.map((v) => <VideoCard key={v.id} v={v} />)}
              </Row>
            )}
            {feed.instagram.length > 0 && (
              <Row icon={Instagram} title="On Instagram" href={feed.instagram_profile} linkLabel="Follow on Instagram" testid="social-instagram-row">
                {feed.instagram.map((p) => <PostCard key={p.id} p={p} />)}
              </Row>
            )}
          </>
        )}
      </div>
    </section>
  );
};
